import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import RoomTypeCapacityForm from '~/components/Common/Forms/RoomTypeCapacityForm';
import RoomTypeOccupancyForm from '~/components/Common/Forms/RoomTypeOccupancyForm';
import PermissionComponent from '~/components/Common/PermissionedComponent';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';
import { ROLE_ADMIN_USER, ROLE_COORDINATOR } from '~/consts/roles';

import ReservationService from '~/services/ReservationService';

import { reportError } from '~/utils/reportError';

import RoomTypeCapacity from './RoomTypeCapacity';

interface RoomTypeCapacitiesProps {
  propertyId: string;
  channelManager?: string;
  roomType: {
    id: string;
  };
  roomRate: {
    id: string;
    disable_automated_occupancy_updates?: boolean;
  };
  capacities: Array<{
    id: string;
    is_dynamic: boolean;
  }>;
  deleteRoomTypeCapacity: (id: string) => void;
  onRequestEdit?: (capacity: unknown) => void;
  onUpdateRoomTypeCapacities: () => void;
  onDeleteAll: () => void;
  isDeletingAll: boolean;
}

export default function RoomTypeCapacities(props: RoomTypeCapacitiesProps) {
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState(null);
  const [disableAutomatedOccupancyUpdates, setDisableAutomatedOccupancyUpdates] = useState(
    Boolean(props.roomRate.disable_automated_occupancy_updates),
  );

  const showConfirmDialog = useConfirmDialog();
  const supportsDisableAutomatedOccupancyUpdates = props.channelManager === PROPERTY_CHANNEL_MANAGERS.derbysoft;

  useEffect(() => {
    setDisableAutomatedOccupancyUpdates(Boolean(props.roomRate.disable_automated_occupancy_updates));
  }, [props.roomRate.id, props.roomRate.disable_automated_occupancy_updates]);

  const openOccupancyModal = () => {
    setMode('occupancy');
  };

  const openEditBox = (capacity) => {
    setMode('capacity');
    setEditing(capacity);
  };

  const closeAddBox = () => {
    setMode(null);
    setEditing(null);
  };

  const onRoomTypeCapacityUpdated = () => {
    props.onUpdateRoomTypeCapacities();
    setMode(null);
    setEditing(null);
  };

  const handleDelete = async () => {
    const confirmed = await showConfirmDialog({
      title: 'Are you sure you want to delete all? This action cannot be undone.',
    });

    if (!confirmed || props.isDeletingAll) return;

    props.onDeleteAll();
  };

  const handleDisableAutomatedOccupancyUpdatesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const flag = event.target.checked;
    setDisableAutomatedOccupancyUpdates(flag);

    try {
      await ReservationService.patchRoomRateDisableAutomatedOccupancyUpdates(
        props.propertyId,
        props.roomType.id,
        props.roomRate.id,
        flag,
      );
    } catch (error) {
      setDisableAutomatedOccupancyUpdates(!flag);
      reportError(error);
    }
  };

  const renderCapacityModal = () => {
    return (
      <Dialog open={true} onClose={closeAddBox}>
        <DialogTitle>Set Room Rate Capacity</DialogTitle>

        <DialogContent>
          <RoomTypeCapacityForm
            capacity={editing}
            propertyId={props.propertyId}
            roomTypeId={props.roomType.id}
            roomRateId={props.roomRate.id}
            onCancel={closeAddBox}
            onRoomTypeCapacityUpdated={onRoomTypeCapacityUpdated}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderOccupancyModal = () => {
    return (
      <Dialog open={true} onClose={closeAddBox}>
        <DialogTitle>Set Room Rate Capacity</DialogTitle>

        <DialogContent>
          <RoomTypeOccupancyForm
            capacity={editing}
            propertyId={props.propertyId}
            roomType={props.roomType}
            roomRate={props.roomRate}
            onCancel={closeAddBox}
            onRoomTypeCapacityUpdated={onRoomTypeCapacityUpdated}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderModal = () => {
    if (mode === 'capacity') {
      return renderCapacityModal();
    }

    if (mode === 'occupancy') {
      return renderOccupancyModal();
    }

    return <Dialog open={false} />;
  };

  const externalProviderData = props.capacities.filter((x) => x.is_dynamic);

  return (
    <Grid className="RoomTypeCapacities">
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Typography color="secondary" textTransform="uppercase">
          Room Rate Capacity
        </Typography>

        {supportsDisableAutomatedOccupancyUpdates && (
          <PermissionComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_COORDINATOR]}>
            <FormControlLabel
              label="Disable automated occupancy updates"
              control={
                <Checkbox
                  id="disable-automated-occupancy-updates-toggle"
                  name="disable-automated-occupancy-updates-toggle"
                  checked={disableAutomatedOccupancyUpdates}
                  onChange={handleDisableAutomatedOccupancyUpdatesChange}
                  size="small"
                />
              }
            />
          </PermissionComponent>
        )}
      </Stack>

      {props.capacities.length === 0 ? (
        <p>
          <i>No capacity set</i>
        </p>
      ) : (
        <div className="room-type-capacity-list">
          {props.capacities.map((capacity) => (
            <RoomTypeCapacity
              key={capacity.id}
              capacity={capacity}
              deleteRoomTypeCapacity={props.deleteRoomTypeCapacity}
              onRequestEdit={openEditBox}
            />
          ))}
        </div>
      )}

      {externalProviderData.length > 0 && (
        <p>
          <i>* Capacities controlled by channel manager</i>
        </p>
      )}

      <PermissionComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_COORDINATOR]}>
        <Stack direction="row" spacing={2} justifyContent="space-between" mt={2}>
          <Stack direction="row" spacing={2}>
            <Button onClick={() => openEditBox(null)} variant="contained" startIcon={<AddIcon />}>
              Add
            </Button>
            <Button onClick={openOccupancyModal} variant="contained" startIcon={<AddIcon />}>
              Advanced
            </Button>
          </Stack>

          {props.capacities && props.capacities.length > 0 && externalProviderData.length == 0 && (
            <div>
              <Button title="Delete all Capacities" variant="text" startIcon={<DeleteIcon />} onClick={handleDelete}>
                Delete All
              </Button>
            </div>
          )}
        </Stack>
      </PermissionComponent>

      {renderModal()}
    </Grid>
  );
}
